import { useMemo } from 'react';

type LinkIcon = 'link' | 'linkedin' | 'instagram' | 'x' | 'youtube' | 'email';

export interface ProfileLink {
  id: string;
  label: string;
  url: string;
  icon?: LinkIcon | string | null;
}

interface ProfileLinkEditorProps {
  links: ProfileLink[];
  onChange: (links: ProfileLink[]) => void;
}

function createLink(): ProfileLink {
  const id = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `link-${Date.now()}`;
  return { id, label: '', url: '', icon: 'link' };
}

const ICON_OPTIONS: { value: LinkIcon; label: string }[] = [
  { value: 'link', label: 'Website' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'x', label: 'X / Twitter' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'email', label: 'Email' },
];

export default function ProfileLinkEditor({ links, onChange }: ProfileLinkEditorProps) {
  const ordered = useMemo(() => links || [], [links]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-cocoa/70">Add links that appear on your public profile.</p>
      <div className="space-y-3">
        {ordered.map((link) => (
          <div key={link.id} className="grid gap-3 rounded-2xl border border-sand/70 bg-white/70 p-4 sm:grid-cols-3">
            <label className="sm:col-span-1 text-sm font-medium text-cocoa">
              Label
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                value={link.label}
                onChange={(e) => onChange(ordered.map((item) => (item.id === link.id ? { ...item, label: e.target.value } : item)))}
                placeholder="My website"
              />
            </label>
            <label className="sm:col-span-1 text-sm font-medium text-cocoa">
              URL
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                value={link.url}
                onChange={(e) => onChange(ordered.map((item) => (item.id === link.id ? { ...item, url: e.target.value } : item)))}
                placeholder="https://example.com"
              />
            </label>
            <label className="text-sm font-medium text-cocoa">
              Icon
              <select
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                value={link.icon || 'link'}
                onChange={(e) => onChange(ordered.map((item) => (item.id === link.id ? { ...item, icon: e.target.value } : item)))}
              >
                {ICON_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <div className="sm:col-span-3 flex justify-end">
              <button type="button" className="text-sm font-medium text-brick" onClick={() => onChange(ordered.filter((item) => item.id !== link.id))}>
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
      <button type="button" className="rounded-xl border border-cocoa px-4 py-2 text-sm font-semibold text-cocoa" onClick={() => onChange([...ordered, createLink()])}>
        Add link
      </button>
    </div>
  );
}
